import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { 
  Modal, Button, Spinner, Toast, ToastContainer, Form, Tab, Tabs, Card, Container, Row, Col 
} from "react-bootstrap";

// Import Config
import { CONFIG } from "./utils/config";

// Import Components
import StatusChecker from "./components/StatusChecker";
import { NavButton } from "./components/CommonUI";

// Import Modules
import LoginScreen from "./modules/LoginScreen";
import DashboardView from "./modules/DashboardView";
import TemplateManager from "./modules/TemplateManager";
import HistoryManager from "./modules/HistoryManager";
import { SingleSmsTool, BulkUploadTool, HelpCenter } from "./modules/Tools";

/**
 * Root Application Component
 */
function App() {
  // Global State
  const [user, setUser] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [historyFilter, setHistoryFilter] = useState("All");
  const [statusLog, setStatusLog] = useState("");
  const [toast, setToast] = useState({ show: false, msg: "", type: "success" });
  const [showSettings, setShowSettings] = useState(false);
  
  // NEW: State for Logout Modal
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Core Utility Functions
  const addLog = useCallback((message, level = 'INFO') => {
    const time = new Date().toLocaleTimeString();
    let text = "";
    if (typeof message === "object") {
      try { text = JSON.stringify(message, null, 2); } catch (e) { text = "[Data Object]"; }
    } else { text = message; }
    const logEntry = `[${time}] [${level}] ${text}`;
    setStatusLog(prevLog => `${logEntry}\n${"-".repeat(60)}\n${prevLog}`);
  }, []);

  const notify = useCallback((msg, type = 'success') => {
    setToast({ show: true, msg, type });
  }, []);

  const saveTransaction = useCallback(async (type, target, templateId, status, response, payload) => {
    try {
      const dbPayload = {
        OperationType: type,
        Target: target,
        TemplateId: templateId,
        Status: status,
        Response: JSON.stringify({ api_response: response, request_payload: payload })
      };
      await axios.post(`${CONFIG.DB_BASE}/history`, dbPayload);
    } catch (e) {
      addLog(`DB SAVE ERROR: ${e.message}`, 'ERROR');
    }
  }, [addLog]);

  // Lifecycle
  useEffect(() => {
    const restoreSession = () => {
      try {
        const savedData = localStorage.getItem("bsnl_user");
        if (savedData) {
          const parsedUser = JSON.parse(savedData);
          if (parsedUser && parsedUser.username) {
            setUser(parsedUser);
            addLog(`Session restored for user: ${parsedUser.username}`, 'SUCCESS');
          }
        }
      } catch (e) {
        console.error("Session Restoration Failed:", e);
        localStorage.removeItem("bsnl_user");
      } finally { setIsSessionLoading(false); }
    };
    restoreSession();
  }, [addLog]);

  const navigateTo = (tabName, filter = "All") => {
    setActiveTab(tabName);
    if (tabName === "history") { setHistoryFilter(filter); }
  };

  // 1. UPDATED: Open the modal instead of window.confirm
  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  // 2. NEW: Perform the actual logout logic
  const confirmLogout = () => {
    localStorage.removeItem("bsnl_user");
    setUser(null);
    setActiveTab("dashboard");
    setStatusLog("");
    setShowLogoutModal(false);
  };

  // --- THEME CONFIGURATION ---
  const themeColor = "#00053c"; 

  if (isSessionLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <div className="text-center">
          <Spinner animation="border" style={{ color: themeColor, width: "3rem", height: "3rem" }} />
          <p className="mt-3 text-secondary fw-bold">Loading Portal...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen onLogin={(u) => { setUser(u); addLog(`User ${u.username} Logged In`, 'SUCCESS'); }} />;
  }

  return (
    <div className="d-flex flex-column min-vh-100 bg-light" style={{ fontFamily: "'Segoe UI', Roboto, sans-serif" }}>
      
      {/* INTERNAL CSS FOR ACTIVE STATES & SCROLLBAR */}
      <style>{`
        .nav-btn-active { background-color: ${themeColor} !important; color: white !important; }
        .nav-btn:hover { background-color: #e9ecef; }
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #1a1a1a; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #444; border-radius: 4px; }
        .bg-theme { background-color: ${themeColor} !important; }
        .text-theme { color: ${themeColor} !important; }
        .btn-theme { background-color: ${themeColor}; border-color: ${themeColor}; color: white; }
        .btn-theme:hover { background-color: #00032a; border-color: #00032a; color: white; }
      `}</style>

      {/* HEADER NAVIGATION */}
      <nav className="navbar navbar-expand-lg navbar-dark shadow-sm px-4 sticky-top bg-theme" style={{ zIndex: 1020 }}>
        <Container fluid>
          <a className="navbar-brand fw-bold d-flex align-items-center" href="#dashboard" onClick={(e) => { e.preventDefault(); navigateTo('dashboard'); }}>
            <i className="bi bi-broadcast-pin me-2 fs-4"></i>
            <span>NSFDC SMS PORTAL</span>
          </a>
          
          <div className="d-flex align-items-center gap-3">
            <div className="text-white text-end d-none d-md-block lh-sm">
              <div className="small opacity-75">Logged in as</div>
              <div className="fw-bold">{user.username}</div>
            </div>
            
            <div className="vr text-white opacity-50 mx-2"></div>
            
            <StatusChecker addLog={addLog} notify={notify} />
            
            <Button variant="link" className="text-white opacity-75 p-0 ms-2" onClick={() => setShowSettings(true)} title="Settings">
                <i className="bi bi-gear-fill fs-5"></i>
            </Button>
            
            <Button variant="danger" size="sm" className="fw-bold px-3 ms-2 rounded-pill" onClick={handleLogout}>
                Logout <i className="bi bi-box-arrow-right ms-1"></i>
            </Button>
          </div>
        </Container>
      </nav>

      {/* MAIN CONTENT GRID */}
      <Container fluid className="py-4 px-4 flex-grow-1">
        <Row className="g-4 h-100">
         
         {/* LEFT COLUMN: NAVIGATION & LOGS */}
         <Col lg={3} md={4} className="d-flex flex-column gap-3">
            
            {/* NAVIGATION CARD */}
            <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
                <Card.Header className="bg-white border-bottom py-3">
                    <h6 className="m-0 fw-bold text-secondary">MENU</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="list-group list-group-flush">
                        {/* Note: Ensure NavButton component accepts 'className' or style props to apply .nav-btn-active.
                           If NavButton is strictly internal, you might need to pass the active color logic there.
                           Assuming standard implementation:
                        */}
                        <NavButton id="dashboard" label="Dashboard" icon="bi-grid-1x2-fill" active={activeTab} setTab={navigateTo} />
                        <NavButton id="templates" label="Template Manager" icon="bi-collection-play-fill" active={activeTab} setTab={navigateTo} />
                        <NavButton id="history" label="History Logs" icon="bi-clock-history" active={activeTab} setTab={navigateTo} />
                        <NavButton id="help" label="Help Center" icon="bi-question-circle-fill" active={activeTab} setTab={navigateTo} />
                    </div>
                </Card.Body>
            </Card>

            {/* CONSOLE CARD */}
            <Card className="shadow-sm border-0 rounded-3 flex-grow-1" style={{minHeight: "350px"}}>
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center py-2 px-3 rounded-top">
                    <span className="small fw-bold"><i className="bi bi-terminal me-2"></i>Activity Log</span>
                    <Button variant="link" className="text-decoration-none text-light p-0 small" style={{fontSize:"0.75rem"}} onClick={() => setStatusLog("")}>Clear</Button>
                </Card.Header>
                <Card.Body className="bg-dark p-0 position-relative rounded-bottom">
                    <div className="position-absolute top-0 start-0 w-100 h-100 overflow-auto p-3 custom-scrollbar">
                        {statusLog ? 
                            <pre className="m-0 text-success" style={{fontFamily: "Consolas, monospace", fontSize: "0.8rem", whiteSpace: "pre-wrap"}}>{statusLog}</pre> 
                            : 
                            <div className="text-secondary text-center mt-5 small">System ready.</div>
                        }
                    </div>
                </Card.Body>
            </Card>
         </Col>

          {/* RIGHT COLUMN: WORKSPACE */}
          <Col lg={9} md={8}>
            <Card className="shadow-sm border-0 h-100 bg-white rounded-3">
              <Card.Body className="p-4">
                {activeTab === 'dashboard' && <DashboardView onDrillDown={navigateTo} />}
                {activeTab === 'templates' && <TemplateManager addLog={addLog} saveTransaction={saveTransaction} notify={notify} />}
                {activeTab === 'history' && <HistoryManager addLog={addLog} initialFilter={historyFilter} notify={notify} />}
                {activeTab === 'single' && <SingleSmsTool addLog={addLog} saveTransaction={saveTransaction} notify={notify} />}
                {activeTab === 'bulk' && <BulkUploadTool addLog={addLog} saveTransaction={saveTransaction} notify={notify} />}
                {activeTab === 'help' && <HelpCenter />}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* GLOBAL TOAST CONTAINER */}
      <ToastContainer position="top-end" className="p-3" style={{zIndex: 9999}}>
        <Toast onClose={() => setToast({...toast, show: false})} show={toast.show} delay={4000} autohide bg={toast.type}>
          <Toast.Header>
              <strong className="me-auto">Notification</strong>
          </Toast.Header>
          <Toast.Body className={toast.type === 'light' ? 'text-dark' : 'text-white'}>{toast.msg}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* SETTINGS MODAL */}
      <Modal show={showSettings} onHide={() => setShowSettings(false)} centered size="lg">
        <Modal.Header closeButton>
            <Modal.Title className="h5 fw-bold">Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs defaultActiveKey="general" id="settings-tabs" className="mb-3">
            <Tab eventKey="general" title="General">
              <Form>
                <h6 className="text-theme mb-3">Preferences</h6>
                <Form.Group className="mb-3"><Form.Check type="switch" id="verbose-log" label="Enable Verbose Logging" defaultChecked /></Form.Group>
                <Form.Group className="mb-3"><Form.Check type="switch" id="auto-refresh" label="Auto-refresh Dashboard" /></Form.Group>
              </Form>
            </Tab>
            <Tab eventKey="api" title="API">
               <Form>
                <h6 className="text-theme mb-3">Configuration</h6>
                <Form.Group className="mb-3"><Form.Label>API Timeout (ms)</Form.Label><Form.Control type="number" defaultValue="30000" /></Form.Group>
                <Form.Group className="mb-3"><Form.Label>Retry Limit</Form.Label><Form.Control type="number" defaultValue="3" /></Form.Group>
               </Form>
            </Tab>
            <Tab eventKey="diag" title="Diagnostics">
               <div className="text-center p-4">
                   <Button variant="outline-primary" onClick={() => notify("Diagnostics Started...", "info")}>Run System Check</Button>
               </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSettings(false)}>Close</Button>
            <Button className="btn-theme" onClick={() => { notify("Saved!", "info"); setShowSettings(false); }}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      {/* LOGOUT CONFIRMATION MODAL */}
      <Modal show={showLogoutModal} onHide={() => setShowLogoutModal(false)} centered backdrop="static" size="sm">
        <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="h5 fw-bold">Logout?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center pt-2 pb-4">
            <p className="text-muted">Are you sure you want to end your session?</p>
            <div className="d-flex justify-content-center gap-2 mt-4">
                <Button variant="light" className="px-4 border" onClick={() => setShowLogoutModal(false)}>Cancel</Button>
                <Button variant="danger" className="px-4" onClick={confirmLogout}>Logout</Button>
            </div>
        </Modal.Body>
      </Modal>

    </div>
  );
}

export default App;